// src/components/views/AnnotatedTab.tsx
import React, { useMemo, useState } from 'react'
import { Content } from '../../../types/models/content'
import ImageGalleryHeader from './ImageGalleryHeader'
import AnnotationsGalleryView from '../AnnotationsGalleryView'
import ErrorDialog from '../../../components/modals/ErrorDialog'
import ExportSnippetsModal from '../../modals/ExportSnippetsModal'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import styled from 'styled-components'
import { Project } from '../../../types/models/project'

// Add this type definition near the top of the file
type ActiveTab = 'content' | 'annotated'

interface AnnotationsSubViewProps {
  project: Project | undefined
  itemsPerPage: number
  onContentSelect: (content: Content) => void
  activeTab: ActiveTab
  onTabSelect: (tab: string | null) => void
  onPageChange: (pageNumber: number) => void
  filteredAnnotatedContent: Content[]
  filteredUnannotatedContent: Content[]
  isAnnotatedLoading: boolean
  isUnannotatedLoading: boolean
  annoStats: any
  errorMessage: string | null
  showError: boolean
  onErrorHide: () => void
  annotatedPage: number
  unannotatedPage: number
}

// Add this styled component with the other styled components
const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
`

const AnnotationsSubView: React.FC<AnnotationsSubViewProps> = ({
  project,
  itemsPerPage,
  onContentSelect,
  activeTab,
  onTabSelect,
  onPageChange,
  filteredAnnotatedContent,
  filteredUnannotatedContent,
  isAnnotatedLoading,
  isUnannotatedLoading,
  annoStats,
  errorMessage,
  showError,
  onErrorHide,
  annotatedPage,
  unannotatedPage,
}) => {
  const [imagesPerRow, setImagesPerRow] = useState(6)

  // Get the current page from the parent component's state
  const currentPage = activeTab === 'annotated' ? annotatedPage : unannotatedPage
  const totalImages =
    activeTab === 'annotated'
      ? annoStats?.statistics?.total_annotated_images || 0
      : annoStats?.statistics?.total_unannotated_images || 0

  // Get the appropriate filtered content based on active tab
  const filteredContent =
    activeTab === 'annotated' ? filteredAnnotatedContent : filteredUnannotatedContent
  const isContentLoading = activeTab === 'annotated' ? isAnnotatedLoading : isUnannotatedLoading

  const [showExport, setShowExport] = useState(false)

  // Build bulk items from filtered content: only keep images that have annotation boxes
  const bulkItems = useMemo(() => {
    const items: Array<{ contentId: string; boxes: AnnotationDataBoundingBox[] }> = []
    for (const c of filteredContent || []) {
      const boxes = (c.annotation || [])
        .flatMap((a) => [
          ...((a.metadata?.bounding_boxes || []) as AnnotationDataBoundingBox[]),
          ...((a.metadata?.oriented_bounding_boxes || []) as AnnotationDataBoundingBox[]),
        ])
        .filter((b) => b) as AnnotationDataBoundingBox[]
      if (boxes.length > 0) items.push({ contentId: c.id, boxes })
    }
    return items
  }, [filteredContent])

  return (
    <ViewContainer>
      <ImageGalleryHeader
        project={project}
        imagesPerRow={imagesPerRow}
        onImagesPerRowChange={setImagesPerRow}
        showTagFilter={true}
        currentPage={currentPage}
        totalImages={totalImages}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        activeTab={activeTab}
        onTabSelect={onTabSelect}
        annoStats={annoStats}
        onBulkExport={() => setShowExport(true)}
      />

      <div className="flex-grow-1 overflow-hidden" style={{ minHeight: 0 }}>
        <AnnotationsGalleryView
          content={filteredContent || []}
          isContentLoading={isContentLoading}
          imagesPerRow={imagesPerRow}
          onContentSelect={onContentSelect}
        />
      </div>

      <ErrorDialog
        show={showError}
        message={errorMessage || 'An error occurred'}
        onHide={onErrorHide}
      />

      {showExport && (
        <ExportSnippetsModal
          show={showExport}
          onHide={() => setShowExport(false)}
          items={bulkItems}
        />
      )}
    </ViewContainer>
  )
}

export default AnnotationsSubView
